import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from '../base.entity';
import {
	ChatChannelType,
	ChatConversationStatus,
	ChatConversationVisibility,
} from './chat-enums';

/**
 * A single chat room — DM, group, or customer-support thread.
 *
 * The channel field is the primary discriminator for access rules:
 * portal users can ONLY be participants in customer_support rows; the
 * MembershipService enforces this at write time so the invariant holds
 * regardless of which entry point creates participants.
 */
@Entity('chat_conversation')
@Index('idx_chat_conversation_status', ['channel', 'status'])
@Index('idx_chat_conversation_linked_client', ['linkedClientId', 'status'])
@Index('idx_chat_conversation_last_message', ['lastMessageAt'])
export class ChatConversationEntity extends BaseEntity {
	@Column('char', { name: 'uuid', length: 36, unique: true })
	public uuid!: string;

	@Column('enum', {
		name: 'channel',
		enum: ChatChannelType,
	})
	public channel!: ChatChannelType;

	/** Null for DMs (rendered client-side from participant names); set for groups + customer_support. */
	@Column('varchar', { name: 'name', nullable: true, length: 255 })
	public name?: string;

	/** Slack-style topic line displayed in the conversation header. */
	@Column('varchar', { name: 'topic', nullable: true, length: 500 })
	public topic?: string;

	@Column('text', { name: 'description', nullable: true })
	public description?: string;

	@Column('enum', {
		name: 'status',
		enum: ChatConversationStatus,
		default: ChatConversationStatus.Active,
	})
	public status!: ChatConversationStatus;

	/**
	 * Visibility model for GROUPS. DMs ignore this (always private,
	 * cannot be changed) and customer_support uses its own routing
	 * rules. Team/role gating looks up the join tables in
	 * chat_conversation_team / chat_conversation_role — an empty set
	 * for either gate means "no one matches", effectively private.
	 */
	@Column('enum', {
		name: 'visibility',
		enum: ChatConversationVisibility,
		default: ChatConversationVisibility.Private,
	})
	public visibility!: ChatConversationVisibility;

	/** For customer_support: the client the portal user represents. Null for DMs/groups. */
	@Column('int', {
		name: 'fk_client__id',
		nullable: true,
		unsigned: true,
	})
	public linkedClientId?: number;

	/** Optional pinning to a specific order (useful for order-context chats). */
	@Column('int', {
		name: 'fk_order__id',
		nullable: true,
		unsigned: true,
	})
	public linkedOrderId?: number;

	/**
	 * Customer-support only: current primary rep. Additional reps can be
	 * participants; this field drives "who does the customer see as their
	 * assigned rep" in the UI and is the first fallback target when the
	 * routing service tries to assign.
	 */
	@Column('int', {
		name: 'assignedRepUserId',
		nullable: true,
		unsigned: true,
	})
	public assignedRepUserId?: number;

	/** Forward reference to chat_queue_entry (Phase 4 — queue not created yet). */
	@Column('int', {
		name: 'fk_chat_queue_entry__id',
		nullable: true,
		unsigned: true,
	})
	public queueEntryId?: number;

	@Column('int', {
		name: 'fk_chat_message__id_latest',
		nullable: true,
		unsigned: true,
	})
	public latestMessageId?: number;

	@Column('datetime', { name: 'lastMessageAt', nullable: true, precision: 3 })
	public lastMessageAt?: Date;

	@Column('int', { name: 'messageCount', default: 0 })
	public messageCount!: number;

	/**
	 * Free-form extension. Currently used for:
	 *   - pre-chat intake form data (customer_support)
	 *   - page-context captured when a customer started the chat
	 */
	@Column('json', { name: 'metadata', nullable: true })
	public metadata?: Record<string, unknown>;
}
